/**
 * Browse an AWS S3 connection. Rendered inside `ConnectionBrowserPage`, which
 * owns the page header and the connection picker (ADR-0021). List buckets →
 * list objects → download; upload; delete an object with a reason.
 *
 * The Blob browser with S3 vocabulary, minus the bucket lifecycle: there is no
 * create/delete bucket, deliberately (ADR-0021 §5), so this view has no
 * admin-only section at all.
 *
 * Credentials resolve server-side. Download streams bytes; upload is
 * multipart; upload and delete are audited server-side.
 */

import { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Button,
  FileInput,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconDownload, IconFileZip, IconRefresh, IconTrash, IconUpload } from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { promptForReason } from "@/shared/ui/promptForReason";
import { toastApiError, toastSuccess, toastWarning } from "@/shared/ui/toast";
import {
  downloadFile,
  s3DeleteFile,
  s3DownloadAll,
  s3FileContentUrl,
  s3ListBuckets,
  s3ListFiles,
  s3UploadFile,
  type BucketSummary,
  type S3ObjectSummary,
} from "@/shared/api/integration-browse";

export function S3Browser({ connectionId }: { connectionId: string }) {
  const getToken = useAccessToken();

  const [buckets, setBuckets] = useState<BucketSummary[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  // Current folder within the bucket ("" = root). S3 keys are flat, so we
  // scope the listing with `prefix` and derive subfolders client-side by
  // splitting the remaining key on "/".
  const [prefix, setPrefix] = useState("");
  const [files, setFiles] = useState<S3ObjectSummary[]>([]);
  const [loadingBuckets, setLoadingBuckets] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadCandidates, setUploadCandidates] = useState<File[]>([]);
  // "3 of 7" while a batch runs, so a slow upload doesn't look stalled.
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadBuckets = useCallback(async () => {
    setLoadingBuckets(true);
    setError(null);
    try {
      const token = await getToken();
      const result = await s3ListBuckets(token, connectionId);
      setBuckets(result.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoadingBuckets(false);
    }
  }, [getToken, connectionId]);

  const loadFiles = useCallback(
    async (bucket: string, atPrefix: string) => {
      setLoadingFiles(true);
      try {
        const token = await getToken();
        const result = await s3ListFiles(token, connectionId, bucket, atPrefix || null);
        setFiles(result.items);
      } catch (err) {
        toastApiError("Could not list objects", err);
        setFiles([]);
      } finally {
        setLoadingFiles(false);
      }
    },
    [getToken, connectionId],
  );

  // Re-scan the store now: reload buckets, and the current folder's objects.
  const onRefresh = useCallback(async () => {
    await loadBuckets();
    if (selected) await loadFiles(selected, prefix);
  }, [loadBuckets, loadFiles, selected, prefix]);

  useEffect(() => {
    void loadBuckets();
  }, [loadBuckets]);

  useEffect(() => {
    if (selected) void loadFiles(selected, prefix);
    else setFiles([]);
  }, [selected, prefix, loadFiles]);

  // Immediate subfolders + objects at the current prefix, derived from the
  // flat listing (names are full keys).
  const subfolders = new Set<string>();
  const filesHere: S3ObjectSummary[] = [];
  for (const f of files) {
    const rest = f.name.startsWith(prefix) ? f.name.slice(prefix.length) : f.name;
    const slash = rest.indexOf("/");
    if (slash > 0) subfolders.add(rest.slice(0, slash));
    else if (rest) filesHere.push(f);
  }
  const folderNames = [...subfolders].sort();
  const crumbs = prefix ? prefix.replace(/\/$/, "").split("/") : [];

  const onDeleteFile = async (obj: S3ObjectSummary) => {
    if (!selected) return;
    const shown = obj.name.split("/").pop() ?? obj.name;
    const reason = await promptForReason(`Delete "${shown}"`, "Audit reason", {
      warning: "This permanently deletes the object. This cannot be undone.",
      confirmLabel: "Delete",
    });
    if (!reason) return;
    setBusy(true);
    try {
      const token = await getToken();
      await s3DeleteFile(token, connectionId, selected, obj.name, reason);
      toastWarning("Object deleted", shown);
      await loadFiles(selected, prefix);
    } catch (err) {
      toastApiError("Delete failed", err);
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async (obj: S3ObjectSummary) => {
    if (!selected) return;
    try {
      const token = await getToken();
      await downloadFile(
        token,
        s3FileContentUrl(connectionId, selected, obj.name),
        obj.name.split("/").pop() ?? obj.name,
      );
    } catch (err) {
      toastApiError("Download failed", err);
    }
  };

  const onDownloadAll = async () => {
    if (!selected) return;
    setBulkBusy(true);
    try {
      const token = await getToken();
      const res = await s3DownloadAll(token, connectionId, selected, prefix || null);
      toastSuccess(
        "Download ready",
        res.truncated
          ? `Zipped the first ${res.count} of ${res.matched} objects (capped).`
          : `Zipped ${res.count} object${res.count === 1 ? "" : "s"}.`,
      );
    } catch (err) {
      toastApiError("Download all failed", err);
    } finally {
      setBulkBusy(false);
    }
  };

  const onUpload = async () => {
    if (!selected || uploadCandidates.length === 0) return;
    setBusy(true);
    // One request per object rather than a bulk endpoint, as for Blob: each
    // gets its own `file.uploaded` audit row naming it, which a single batched
    // event could not provide. Sequential so a large batch can't swamp the
    // bucket or the API.
    const failed: string[] = [];
    let uploaded = 0;
    try {
      const token = await getToken();
      for (const [index, file] of uploadCandidates.entries()) {
        setUploadProgress(`${index + 1} of ${uploadCandidates.length}`);
        try {
          await s3UploadFile(token, connectionId, selected, `${prefix}${file.name}`, file);
          uploaded += 1;
        } catch {
          // One bad object must not abandon the rest — collect and report at
          // the end. A 409 means the key exists and overwrite was not asked
          // for; the server refuses to clobber silently.
          failed.push(file.name);
        }
      }
      if (uploaded > 0) {
        toastSuccess(
          uploaded === 1 ? "Uploaded" : `Uploaded ${uploaded} objects`,
          `${selected}/${prefix}`,
        );
      }
      if (failed.length > 0) {
        // Named, not counted: "3 failed" is not actionable.
        toastApiError(
          `${failed.length} object${failed.length === 1 ? "" : "s"} failed`,
          new Error(failed.join(", ")),
        );
      }
      setUploadCandidates([]);
      await loadFiles(selected, prefix);
    } catch (err) {
      toastApiError("Upload failed", err);
    } finally {
      setUploadProgress(null);
      setBusy(false);
    }
  };

  return (
    <Stack gap="md">
      {/* The page header and connection picker belong to ConnectionBrowserPage;
          Refresh stays here because it acts on this browser's own data. */}
      <Group justify="flex-end">
        <Button
          variant="default"
          size="xs"
          leftSection={<IconRefresh size={16} />}
          onClick={() => void onRefresh()}
          loading={loadingBuckets || loadingFiles}
        >
          Refresh
        </Button>
      </Group>

      {error && (
        <Alert variant="light" color="red" title="Could not load buckets">
          {error}
        </Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Select
          label="Bucket"
          placeholder={loadingBuckets ? "Loading…" : "Select a bucket"}
          data={buckets.map((b) => b.name)}
          value={selected}
          onChange={(v) => {
            setSelected(v);
            setPrefix(""); // start at the bucket root
          }}
          searchable
        />
      </Paper>

      {selected && (
        <Paper withBorder radius="md" p="md">
          <Group align="flex-end" gap="sm">
            <FileInput
              multiple
              label="Upload files"
              description={
                uploadProgress
                  ? `Uploading ${uploadProgress}…`
                  : "Pick one or several. Each is uploaded and audited separately."
              }
              placeholder="Choose files…"
              value={uploadCandidates}
              onChange={setUploadCandidates}
              clearable
              style={{ flex: 1 }}
            />
            <Button
              leftSection={<IconUpload size={16} />}
              onClick={() => void onUpload()}
              loading={busy && uploadProgress !== null}
              disabled={busy || uploadCandidates.length === 0}
            >
              {uploadCandidates.length > 1 ? `Upload ${uploadCandidates.length}` : "Upload"}
            </Button>
          </Group>
        </Paper>
      )}

      {selected && (
        <Group gap={4} wrap="wrap">
          <Anchor size="sm" onClick={() => setPrefix("")}>
            {selected}
          </Anchor>
          {crumbs.map((seg, i) => (
            <Group key={i} gap={4} wrap="nowrap">
              <Text size="sm" c="dimmed">
                /
              </Text>
              <Anchor size="sm" onClick={() => setPrefix(crumbs.slice(0, i + 1).join("/") + "/")}>
                {seg}
              </Anchor>
            </Group>
          ))}
        </Group>
      )}

      {selected && filesHere.length > 0 && (
        <Group justify="flex-end">
          <Button
            variant="default"
            size="xs"
            leftSection={<IconFileZip size={16} />}
            onClick={() => void onDownloadAll()}
            loading={bulkBusy}
          >
            Download all in this folder
          </Button>
        </Group>
      )}

      <Paper withBorder radius="md" p={0}>
        {!selected ? (
          <Text c="dimmed" p="xl" ta="center">
            Select a bucket to browse.
          </Text>
        ) : loadingFiles ? (
          <Group justify="center" p="xl">
            <Loader size="sm" />
          </Group>
        ) : folderNames.length === 0 && filesHere.length === 0 ? (
          <Text c="dimmed" p="xl" ta="center">
            This folder is empty.
          </Text>
        ) : (
          <Table verticalSpacing="sm" striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Size</Table.Th>
                <Table.Th>Modified</Table.Th>
                <Table.Th style={{ width: 96 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {folderNames.map((name) => (
                <Table.Tr
                  key={`dir:${name}`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setPrefix(`${prefix}${name}/`)}
                >
                  <Table.Td>📁 {name}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      folder
                    </Text>
                  </Table.Td>
                  <Table.Td />
                </Table.Tr>
              ))}
              {filesHere.map((f) => (
                <Table.Tr key={f.name}>
                  <Table.Td>{f.name.slice(prefix.length)}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {formatBytes(f.size_bytes)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {/* S3 listings carry no content type, so the modified
                        stamp is the more useful third column here. */}
                    <Text size="sm" c="dimmed">
                      {f.last_modified ? new Date(f.last_modified).toLocaleString() : "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={2} wrap="nowrap" justify="flex-end">
                      <Tooltip label="Download">
                        <ActionIcon
                          variant="subtle"
                          color="brand"
                          onClick={() => void onDownload(f)}
                          aria-label={`Download ${f.name}`}
                        >
                          <IconDownload size={18} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          disabled={busy}
                          onClick={() => void onDeleteFile(f)}
                          aria-label={`Delete ${f.name}`}
                        >
                          <IconTrash size={18} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
